import { defineStore, acceptHMRUpdate } from 'pinia';

import { getServices } from '~/db/services';

import type { Config } from '~/types/app';

export const useConfigStore = defineStore('ConfigStore', () => {
  if (!useAppRunningStore().isRunning) return;

  const { $db } = useNuxtApp();
  const { configsService } = getServices($db);

  const data = ref<Config>(null);

  const { config: configDataSync } = useConfigSyncGet();

  watchEffect(() => {
    if (configDataSync.value && configDataSync.value.length > 0) {
      data.value = configDataSync.value[0].data;
    } else {
      data.value = null;
    }
  });

  const loadFromClient = async () => {
    const configData = await configsService.get({ id: '1' });
    if (configData) {
      forceUpdateRawValue(configData.data);
      return true;
    }
    return false;
  };

  const forceUpdateRawValue = (rawValue) => {
    data.value = rawValue;
  };

  const contentSynchroFrequencyHour = computed(
    () => data.value?.contentSynchroFrequencyHour ?? 1000
  );

  const businessSynchroFrequencyHour = computed(
    () => data.value?.businessSynchroFrequencyHour ?? 1000
  );

  const userSynchroFrequencyHour = computed(
    () => data.value?.userSynchroFrequencyHour ?? 1000
  );

  const multiProgressionLimit = computed(
    () => data.value?.multiProgressionLimit ?? 1000
  );

  const maxUnsubscribedAssessments = computed(
    () => data.value?.maxUnsubscribedAssessments ?? 10
  );

  const avatarIds = computed(() => data.value?.avatarIds ?? []);

  const appId = computed(() => data.value?.appId ?? 'noContentId');

  const featureFlags = computed(() => {
    const featureFlags = data.value?.featureFlags || {};
    return {
      ssoAuthApple: false,
      ssoAuthFacebook: false,
      ssoAuthGoogle: false,
      ssoAuthTiktok: false,
      exerciseResultSendParentEmail: true,
      ...featureFlags,
    };
  });

  const isSocialAuhAvailable = computed(() => {
    const items = featureFlags.value;
    return (
      items.ssoAuthApple ||
      items.ssoAuthFacebook ||
      items.ssoAuthGoogle ||
      items.ssoAuthTiktok
    );
  });

  const searchContentConfig = computed(() => {
    return data.value?.searchContentConfig;
  });

  const trialContentCount = computed(() => {
    return data.value?.trialContentCount || 1;
  });

  return {
    data: data as Readonly<Ref<Config>>,
    forceUpdateRawValue,
    loadFromClient,
    userSynchroFrequencyHour,
    contentSynchroFrequencyHour,
    businessSynchroFrequencyHour,
    multiProgressionLimit,
    maxUnsubscribedAssessments,
    appId,
    avatarIds,
    featureFlags,
    isSocialAuhAvailable,
    searchContentConfig,
    trialContentCount,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConfigStore, import.meta.hot));
}
